import * as THREE from '../../libs/three.js/build/three.module.js';
import { CameraMode, ClipTask } from '../defines.js';
import { BoxVolume } from '../utils/Volume.js';

/**
 * Create a clip box volume for a floor Z band, sized to the first point cloud XY extent.
 */
export function createFloorClipVolume(viewer, zMin, zMax) {
  const pointcloud = viewer.scene.pointclouds[0];
  if (!pointcloud) {
    return null;
  }

  pointcloud.updateMatrixWorld(true);
  const bbox = pointcloud.boundingBox.clone();
  bbox.applyMatrix4(pointcloud.matrixWorld);

  const cx = (bbox.min.x + bbox.max.x) / 2;
  const cy = (bbox.min.y + bbox.max.y) / 2;
  const cz = (zMin + zMax) / 2;
  const sx = Math.max(bbox.max.x - bbox.min.x, 0.01);
  const sy = Math.max(bbox.max.y - bbox.min.y, 0.01);
  const sz = Math.max(zMax - zMin, 0.01);

  const volume = new BoxVolume({ clip: true });
  volume.name = 'Floor clip';
  volume.position.set(cx, cy, cz);
  volume.scale.set(sx, sy, sz);
  viewer.scene.addVolume(volume);
  return volume;
}

/**
 * Restore clip volume transform from saved metadata.
 */
export function applyClipVolumeFromSaved(volume, saved) {
  if (!volume || !saved) return;
  if (saved.clip_position_x != null) {
    volume.position.set(
      saved.clip_position_x,
      saved.clip_position_y,
      saved.clip_position_z
    );
  }
  if (saved.clip_scale_x != null) {
    volume.scale.set(saved.clip_scale_x, saved.clip_scale_y, saved.clip_scale_z);
  }
  volume.clip = true;
  volume.updateMatrixWorld(true);
}

export function readClipVolumeState(volume) {
  if (!volume) return null;
  volume.updateMatrixWorld(true);
  return {
    clip_position_x: volume.position.x,
    clip_position_y: volume.position.y,
    clip_position_z: volume.position.z,
    clip_scale_x: volume.scale.x,
    clip_scale_y: volume.scale.y,
    clip_scale_z: volume.scale.z,
  };
}

/** Unit-box corners for Potree BoxVolume (geometry is 1×1×1, centered at origin). */
const UNIT_BOX_CORNERS = [
  [-0.5, -0.5, -0.5],
  [0.5, -0.5, -0.5],
  [0.5, 0.5, -0.5],
  [-0.5, 0.5, -0.5],
  [-0.5, -0.5, 0.5],
  [0.5, -0.5, 0.5],
  [0.5, 0.5, 0.5],
  [-0.5, 0.5, 0.5],
];

/**
 * World-space AABB from volume transform. Avoids Box3.setFromObject(), which
 * inflates bounds for BoxVolume at large scene coords (e.g. PL-2000).
 */
function getVolumeWorldBox(volume) {
  volume.updateMatrixWorld(true);
  const box = new THREE.Box3();
  const corner = new THREE.Vector3();
  for (const [x, y, z] of UNIT_BOX_CORNERS) {
    corner.set(x, y, z).applyMatrix4(volume.matrixWorld);
    box.expandByPoint(corner);
  }
  if (box.isEmpty()) {
    throw new Error('CLIP_VOLUME_REQUIRED');
  }
  return box;
}

export function computeMapExtentFromVolume(mapView, volume) {
  if (!mapView?.sceneProjection || !mapView.toMap) {
    throw new Error('MAP_PROJECTION_REQUIRED');
  }

  const worldBox = getVolumeWorldBox(volume);
  const corners = [
    new THREE.Vector3(worldBox.min.x, worldBox.min.y, worldBox.min.z),
    new THREE.Vector3(worldBox.max.x, worldBox.min.y, worldBox.min.z),
    new THREE.Vector3(worldBox.max.x, worldBox.max.y, worldBox.min.z),
    new THREE.Vector3(worldBox.min.x, worldBox.max.y, worldBox.min.z),
  ];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const c of corners) {
    const mapped = mapView.toMap.forward([c.x, c.y]);
    minX = Math.min(minX, mapped[0]);
    minY = Math.min(minY, mapped[1]);
    maxX = Math.max(maxX, mapped[0]);
    maxY = Math.max(maxY, mapped[1]);
  }

  if (!Number.isFinite(minX) || minX >= maxX || minY >= maxY) {
    throw new Error('INVALID_MAP_EXTENT');
  }

  return [minX, minY, maxX, maxY];
}

function saveViewState(viewer) {
  const view = viewer.scene.view;
  return {
    cameraMode: viewer.scene.cameraMode,
    clipTask: viewer.getClipTask(),
    edlEnabled: viewer.getEDLEnabled?.() ?? null,
    yaw: view.yaw,
    pitch: view.pitch,
    radius: view.radius,
    position: view.position.clone(),
  };
}

function restoreViewState(viewer, saved) {
  if (!saved) return;
  viewer.setCameraMode(saved.cameraMode);
  viewer.setClipTask(saved.clipTask);
  if (saved.edlEnabled != null && viewer.setEDLEnabled) {
    viewer.setEDLEnabled(saved.edlEnabled);
  }
  const view = viewer.scene.view;
  view.yaw = saved.yaw;
  view.pitch = saved.pitch;
  view.radius = saved.radius;
  view.position.copy(saved.position);
}

function computeOrthoRadius(viewer, worldBox) {
  const size = worldBox.getSize(new THREE.Vector3());
  const el = viewer.renderArea;
  const width = el?.clientWidth || 1;
  const height = el?.clientHeight || 1;
  const aspect = width / height;
  const margin = 1.05;
  const halfW = (size.x / 2) * margin;
  const halfH = (size.y / 2) * margin;
  // renderDefault: visible width = 2*radius, height = 2*radius/aspect
  return Math.max(halfW, halfH * aspect, 1);
}

/**
 * Top-down orthographic view on worldBox (Z-up).
 * Camera sits above the top face; rays go straight down. With depth testing,
 * each pixel shows the first surface hit when moving top→bottom through the box
 * (topmost points in the clip band) — floor-plan / roof projection semantics.
 * Do not call lookAt() — it overwrites view.radius (ortho half-width).
 */
function fitTopDownViewToBox(viewer, worldBox) {
  const view = viewer.scene.view;
  const center = worldBox.getCenter(new THREE.Vector3());
  const radius = computeOrthoRadius(viewer, worldBox);
  const topZ = worldBox.max.z;

  view.yaw = 0;
  view.pitch = -Math.PI / 2;
  view.radius = radius;
  // position + (0,0,-1)*radius = pivot on top face center (cx, cy, topZ)
  view.position.set(center.x, center.y, topZ + radius);
}

/** Limit ortho near/far to the clip box depth slab (after viewer.update). */
function applyOrthoDepthRangeFromBox(viewer, worldBox) {
  const cam = viewer.scene.cameraO;
  if (!cam) return;

  cam.updateMatrixWorld();
  const inv = cam.matrixWorldInverse;
  const corner = new THREE.Vector3();
  let minVsZ = Infinity;
  let maxVsZ = -Infinity;

  for (const [x, y, z] of [
    [worldBox.min.x, worldBox.min.y, worldBox.min.z],
    [worldBox.max.x, worldBox.min.y, worldBox.min.z],
    [worldBox.max.x, worldBox.max.y, worldBox.min.z],
    [worldBox.min.x, worldBox.max.y, worldBox.min.z],
    [worldBox.min.x, worldBox.min.y, worldBox.max.z],
    [worldBox.max.x, worldBox.min.y, worldBox.max.z],
    [worldBox.max.x, worldBox.max.y, worldBox.max.z],
    [worldBox.min.x, worldBox.max.y, worldBox.max.z],
  ]) {
    corner.set(x, y, z).applyMatrix4(inv);
    minVsZ = Math.min(minVsZ, corner.z);
    maxVsZ = Math.max(maxVsZ, corner.z);
  }

  const margin = 2.0;
  cam.near = Math.max(0.1, -maxVsZ - margin);
  cam.far = Math.max(cam.near + 1.0, -minVsZ + margin);
  cam.updateProjectionMatrix();
}

function syncSceneCamerasFromView(viewer) {
  const scene = viewer.scene;
  const view = scene.view;

  scene.cameraP.position.copy(view.position);
  scene.cameraP.rotation.order = 'ZXY';
  scene.cameraP.rotation.x = Math.PI / 2 + view.pitch;
  scene.cameraP.rotation.z = view.yaw;

  scene.cameraO.position.copy(view.position);
  scene.cameraO.rotation.order = 'ZXY';
  scene.cameraO.rotation.x = Math.PI / 2 + view.pitch;
  scene.cameraO.rotation.z = view.yaw;

  scene.cameraP.updateMatrixWorld();
  scene.cameraO.updateMatrixWorld();
}

async function prepareCaptureFrame(viewer, worldBox, passes = 2) {
  for (let i = 0; i < passes; i++) {
    syncSceneCamerasFromView(viewer);
    if (typeof viewer.update === 'function') {
      viewer.update(0, performance.now());
    }
    applyOrthoDepthRangeFromBox(viewer, worldBox);
    if (typeof TWEEN !== 'undefined' && TWEEN.update) {
      TWEEN.update(performance.now());
    }
    viewer.render();
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
}

function restoreRendererAfterCapture(viewer) {
  if (viewer?.renderer?.setRenderTarget) {
    viewer.renderer.setRenderTarget(null);
  }
}

function captureCanvasFromViewer(viewer) {
  if (!viewer?.renderer?.domElement) {
    throw new Error('SCREENSHOT_UNAVAILABLE');
  }

  const renderer = viewer.renderer;
  const size = renderer.getSize(new THREE.Vector2());
  const width = Math.floor(size.x);
  const height = Math.floor(size.y);
  if (width <= 0 || height <= 0) {
    throw new Error('SCREENSHOT_EMPTY');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('SCREENSHOT_UNAVAILABLE');
  }

  ctx.drawImage(renderer.domElement, 0, 0, width, height);
  return { canvas, width, height };
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('PNG_ENCODE_FAILED'));
      },
      'image/png',
      0.92
    );
  });
}

/**
 * Capture a floor-plan image: orthographic top-down view of the clip box with
 * depth test (first surface along the downward ray through each pixel).
 */
export async function captureFloorPlan(viewer, clipVolume) {
  const mapView = viewer.mapView;
  if (!mapView?.sceneProjection || !mapView.toMap) {
    throw new Error('MAP_PROJECTION_REQUIRED');
  }
  if (!clipVolume) {
    throw new Error('CLIP_VOLUME_REQUIRED');
  }

  const savedView = saveViewState(viewer);

  try {
    clipVolume.clip = true;
    viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);
    viewer.setClipTask(ClipTask.SHOW_INSIDE);
    if (viewer.setEDLEnabled) {
      viewer.setEDLEnabled(false);
    }

    const worldBox = getVolumeWorldBox(clipVolume);
    fitTopDownViewToBox(viewer, worldBox);
    await prepareCaptureFrame(viewer, worldBox);

    const { canvas, width, height } = captureCanvasFromViewer(viewer);
    const blob = await canvasToPngBlob(canvas);
    const extent = computeMapExtentFromVolume(mapView, clipVolume);
    const clip = readClipVolumeState(clipVolume);

    return {
      blob,
      width,
      height,
      extent,
      clip,
    };
  } finally {
    restoreViewState(viewer, savedView);
    restoreRendererAfterCapture(viewer);
  }
}
